import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { FileText, Activity, ShieldAlert, CheckCircle, Clock, TrendingUp, Search, Filter } from 'lucide-react';
import { KPICard } from '../components/KPICard';
import { StatusBadge } from '../components/StatusBadge';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Button } from '../components/Button';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../data/store';
import { SyncButton } from '../components/SyncButton';
import { FilterBar, FilterField } from '../components/FilterBar';
export default function AnalystDashboard() {
  const navigate = useNavigate();
  const reportsList = useStore(s => s.reports);
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const filterFields: FilterField[] = [{
    key: 'search',
    label: 'Pencarian',
    type: 'search',
    placeholder: 'Cari ID, judul...'
  }, {
    key: 'urgency',
    label: 'Urgensi',
    type: 'urgencyBadge',
    options: [{
      label: 'Rendah',
      value: 'RENDAH'
    }, {
      label: 'Menengah',
      value: 'MENENGAH'
    }, {
      label: 'Tinggi',
      value: 'TINGGI'
    }, {
      label: 'Kritis',
      value: 'KRITIS'
    }]
  }, {
    key: 'date',
    label: 'Tanggal',
    type: 'datePreset'
  }];
  const pendingReports = reportsList.filter(r => {
    const search = filterValues.search?.toLowerCase() || '';
    const matchesSearch = !search || r.title.toLowerCase().includes(search) || r.id.toLowerCase().includes(search);
    const matchesUrgency = !filterValues.urgency || filterValues.urgency === 'all' || r.urgency === filterValues.urgency;
    return (r.stage === 'Review Koordinator' || r.stage === 'Perlu Revisi') && matchesSearch && matchesUrgency;
  });
  const activeAnalysis = reportsList.filter(r => {
    const search = filterValues.search?.toLowerCase() || '';
    const matchesSearch = !search || r.title.toLowerCase().includes(search) || r.id.toLowerCase().includes(search);
    const matchesUrgency = !filterValues.urgency || filterValues.urgency === 'all' || r.urgency === filterValues.urgency;
    return r.stage === 'Analisis' && matchesSearch && matchesUrgency;
  });
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-752">
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-753">
        <div data-id="element-754">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground" data-id="element-755">
            MEJA KERJA ANALIS
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-756">
            Verifikasi Data & Analisis Ancaman
          </p>
        </div>
        <div className="flex gap-3 items-center" data-id="element-757">
          <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-758">
            <Filter className="w-4 h-4 mr-2" data-id="element-759" />
            Filter
          </Button>
          <SyncButton data-id="element-760" />
        </div>
      </div>

      {showFilters && <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} data-id="element-761" />}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-id="element-762">
        <KPICard title="Menunggu Verifikasi" value={pendingReports.length} icon={FileText} colorVar="var(--status-standby)" trend={{
        value: 'PRIORITAS TINGGI',
        label: '',
        isPositive: false
      }} data-id="element-763" />
        <KPICard title="Sedang Dianalisis" value={activeAnalysis.length} icon={Activity} colorVar="var(--status-info)" data-id="element-764" />
        <KPICard title="Laporan Selesai (Hari Ini)" value="12" icon={CheckCircle} colorVar="var(--status-active)" trend={{
        value: '+3',
        label: 'vs kemarin',
        isPositive: true
      }} data-id="element-765" />
        <KPICard title="Rata-rata Waktu Analisis" value="45m" icon={Clock} colorVar="var(--primary)" data-id="element-766" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-767">
        {/* Verification Queue */}
        <Card className="lg:col-span-2 glass-panel border-border/50 flex flex-col overflow-hidden" data-id="element-768">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-row items-center justify-between shrink-0" data-id="element-769">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-770">
              <ShieldAlert className="w-4 h-4 text-primary" data-id="element-771" /> Antrean
              Verifikasi
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-auto" data-id="element-772">
            <Table data-id="element-773">
              <TableHeader className="bg-black/20" data-id="element-774">
                <TableRow className="border-border/30 hover:bg-transparent" data-id="element-775">
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-776">
                    ID Laporan
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-777">
                    Judul
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-778">
                    Urgensi
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-779">
                    Waktu
                  </TableHead>
                  <TableHead className="text-right text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-780">
                    Aksi
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-781">
                {pendingReports.map(report => <TableRow key={report.id} className="border-border/20 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => navigate(`/reports/${report.id}`)} data-id="element-782">
                    <TableCell className="font-mono text-xs text-primary" data-id="element-783">
                      {report.id}
                    </TableCell>
                    <TableCell className="font-medium text-sm" data-id="element-784">
                      {report.title}
                    </TableCell>
                    <TableCell data-id="element-785">
                      <UrgencyBadge urgency={report.urgency} data-id="element-786" />
                    </TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground" data-id="element-787">
                      {report.time.split(' ')[1]}
                    </TableCell>
                    <TableCell className="text-right" data-id="element-788">
                      <Button size="sm" variant="outline" className="text-[10px] font-mono h-7" onClick={e => {
                    e.stopPropagation();
                    navigate(`/reports/${report.id}`);
                  }} data-id="element-789">
                        Verifikasi
                      </Button>
                    </TableCell>
                  </TableRow>)}
                {pendingReports.length === 0 && <TableRow data-id="element-790">
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground font-mono text-xs" data-id="element-791">
                      Tidak ada laporan menunggu verifikasi.
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Active Analysis */}
        <Card className="lg:col-span-1 glass-panel border-border/50 flex flex-col overflow-hidden" data-id="element-792">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 shrink-0" data-id="element-793">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-794">
              <Activity className="w-4 h-4 text-primary" data-id="element-795" /> Sedang Dianalisis
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-auto" data-id="element-796">
            <div className="divide-y divide-border/50" data-id="element-797">
              {activeAnalysis.map(report => <div key={report.id} className="p-4 hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => navigate(`/reports/${report.id}`)} data-id="element-798">
                  <div className="flex justify-between items-start mb-2" data-id="element-799">
                    <span className="font-mono text-xs text-primary" data-id="element-800">
                      {report.id}
                    </span>
                    <UrgencyBadge urgency={report.urgency} data-id="element-801" />
                  </div>
                  <div className="font-heading font-semibold text-sm mb-2" data-id="element-802">
                    {report.title}
                  </div>
                  <div className="flex justify-between items-center text-[10px] font-mono text-muted-foreground" data-id="element-803">
                    <span data-id="element-804">{report.region}</span>
                    <span data-id="element-805">{report.time.split(' ')[1]}</span>
                  </div>
                </div>)}
              {activeAnalysis.length === 0 && <div className="p-8 text-center text-muted-foreground font-mono text-xs" data-id="element-806">
                  Tidak ada analisis aktif.
                </div>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}